import { ProductDetailInfo } from "./ProductDetailInfo";
import { AddToFavoritesButton } from "./AddToFavoritesButton";
import React from "react";
import { ProductDetailHeader } from "./ProductDetailHeader";
import { TrackProductButton } from "./TrackProductButton";
import { DealSelectorBis } from "./DealSelectorBis";
import ProductPurchase from "./ProductPurchase";
import { NumberInputUI } from "../../ui/components/NumberInputUI";
import { RoutePathBreadcrumb } from "../../ui";
import { findItemAvailability } from "../../common/CatalogService";
import { useProductSelection } from "../application/useProductSelection";
import { AddToCartButton } from "./AddToCartButton";

/**
 * @param {{
 *     product,
 *     onProductSelection
 * }} props
 * @constructor
 */
export const ProductDetail = ({ product }) => {
    const {
        productSelection,
        dealSelection,
        setDealSelection,
        setQuantity
    } = useProductSelection(product, 1);


    const hasStock = React.useMemo(
        () => product.availableUnits > 0 || product.onDemand,
        [product.availableUnits, product.onDemand]
    );

    const header = React.useMemo(() => ({
        id: product.id,
        code: product.code,
        name: product.name,
        provider: product.manufacturer,
        price: !product.price ? null : { ...product.price },
        availability: findItemAvailability(product, productSelection.quantity),
        hasStock
    }), [product, productSelection.quantity, hasStock]);

    const info = React.useMemo(() => ({
        imageUrl: product.imageUrl,
        secondaryImages: product.secondaryImages,
        description: product.description,
        cimavetFileUrl: product.cimavetFileUrl,
        needsPrescription: product.needsPrescription,
        needsRefrigeration: product.needsRefrigeration,
        isPsicotropic: product.isPsicotropic,
        isNarcotic: product.isNarcotic,
        isAntibiotic: product.isAntibiotic,
        aiSummary: product.aiSummary
    }), [product]);

    const dealData = React.useMemo(() => ({
        offers: product.offers,
        lots: product.lots,
        promos: product.promos,
        hasDealsToChoose: product.hasDealsToChoose
    }), [product]);

    const recap = React.useMemo(() => {
        if (!productSelection?.price || !productSelection?.discount) {
            return;
        }
        return ({
            price: {
                value: productSelection.price.basePrice * productSelection.quantity,
                bestPrice: productSelection.price.sellingPrice * productSelection.quantity
            },
            discount: {
                value: productSelection.discount.value,
                percentage: productSelection.discount.percentage,
                line: productSelection.discount.text
            }
        })
    }, [productSelection]);

    const showTrackButton = React.useMemo(() =>
        product.tracked || !header.hasStock,
        [product.tracked, header.hasStock]
    );

    return (
        <section className="row">
            <div className="col-12 mb-2">
                <RoutePathBreadcrumb
                    customBones={{
                        [product.code]: {
                            name: product.name,
                            path: `/articulos/${product.code}`,
                        }
                    }}
                />
            </div>
            <div className="col-12 col-lg-6 order-lg-last d-flex flex-column gap-4">
                <ProductDetailHeader header={header}>
                    {showTrackButton &&
                        <ProductDetailHeader.Track>
                            <TrackProductButton
                                productCode={product.code}
                                defaultValue={product.tracked}
                                showText={true}
                            />
                        </ProductDetailHeader.Track>
                    }
                </ProductDetailHeader>

                <DealSelectorBis dealData={dealData} selection={dealSelection} onSelectionChange={setDealSelection} />

                <ProductPurchase recap={recap}>
                    <ProductPurchase.PurchaseControls>
                        <div
                            className="d-flex gap-4 align-items-center justify-content-center justify-content-xl-end">
                            <NumberInputUI data={{ value: productSelection.quantity }} actions={{ setValue: setQuantity }} />
                            <AddToCartButton productSelection={productSelection} hasStock={hasStock} />
                        </div>
                    </ProductPurchase.PurchaseControls>
                </ProductPurchase>
            </div>
            <div className="col-12 col-lg-6">
                <ProductDetailInfo info={info}>
                    <ProductDetailInfo.AddToFavorites>
                        <AddToFavoritesButton data={{
                            productCode: product.code,
                            show: true,
                            isFavorite: product.isFavorite
                        }} />
                    </ProductDetailInfo.AddToFavorites>
                </ProductDetailInfo>
            </div>
        </section>
    );
}